import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, KeyRound } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ChangePasswordDialog } from "./ChangePassword";

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

async function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export default function ProfileEditDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [picture, setPicture] = useState(null); // null = unchanged, string = replace, "" = clear
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setPhone(user?.phone || "");
      setPicture(null);
      setPreview(user?.picture || null);
    }
  }, [open, user]);

  const pickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      toast.error("Please pick a PNG, JPG, WEBP or GIF image");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be under 4 MB");
      return;
    }
    try {
      const dataUrl = await readAsDataURL(file);
      setPicture(dataUrl);
      setPreview(dataUrl);
    } catch {
      toast.error("Could not read image");
    }
  };
  const clearAvatar = () => { setPicture(""); setPreview(null); };

  const save = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) { toast.error("Please enter your name"); return; }
    const payload = {};
    if (trimmed !== (user?.name || "")) payload.name = trimmed;
    const trimmedPhone = phone.trim();
    if (trimmedPhone !== (user?.phone || "")) payload.phone = trimmedPhone;
    if (picture !== null) payload.picture = picture;
    if (Object.keys(payload).length === 0) { onOpenChange(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", payload);
      setUser(data);
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-edit-dialog">
        <DialogHeader><DialogTitle>Edit your profile</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar name={name || user?.name} src={preview} size={64} />
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onFile} data-testid="profile-avatar-input" />
              <Button type="button" variant="outline" size="sm" onClick={pickFile} data-testid="profile-avatar-pick">
                <Camera className="mr-1.5 h-4 w-4" /> {preview ? "Change photo" : "Upload photo"}
              </Button>
              {preview && (
                <Button type="button" variant="outline" size="sm" onClick={clearAvatar} data-testid="profile-avatar-clear" className="text-rose-600 hover:text-rose-700">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Remove
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="profile-name">Name</Label>
            <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="profile-name-input" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="profile-phone">Phone <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
            <Input id="profile-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={32} placeholder="e.g. +27 82 123 4567" data-testid="profile-phone-input" />
          </div>

          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled readOnly className="bg-slate-50 text-slate-500" />
            <p className="text-xs text-slate-400">Email is tied to your sign-in and can't be changed here.</p>
          </div>

          {user?.auth_type !== "google" && user?.auth_type !== "phone" && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <ChangePasswordDialog trigger={
                <Button type="button" variant="outline" size="sm" data-testid="profile-change-password">
                  <KeyRound className="mr-1.5 h-4 w-4" /> Change password
                </Button>
              } />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="profile-save" className="bg-[#4338CA] hover:bg-[#3730A3]">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
